import {
  PhoneAndroidOutlined as DevicesIcon,
  DashboardOutlined as DashboardIcon,
  Menu as MenuIcon,
  PersonOutlineOutlined as DeviceOwnersIcon,
  VerifiedUserOutlined as PoliciesIcon,
  GridViewOutlined as AppsIcon,
  CloudUploadOutlined as RequestAnalysisIcon,
  NotificationsOutlined as NotificationsIcon,
  Devices as AppManagementIcon,
  ReportOutlined as RiskIndicatorsIcon,
  SupportAgentOutlined as CustomerSupportIcon,
  Style,
} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Avatar, Box, Divider, IconButton, ListItemIcon, MenuItem, MenuList } from '@mui/material';
import { QToolbar, QTypography, QHeaderIconButton } from '@kw/quokka-ui';
import { css, styled } from '@mui/material/styles';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { KwAppBar } from '../shared/AppBar';
import { drawerWidth, DashboardDrawer } from './Drawer';
import { NavListItemText } from './ListItemText';
import { Link } from '../../Link';
import SettingsDropDownMenu from '../Settings/SettingsDropDownMenu';
import { DeviceProfileHeader } from '../Devices/DeviceProfileHeader';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { PolicyProfileHeader } from '../Policy/PolicyProfileHeader';
import { AppManagementSearchHeader } from '../AppManagement/AppManagementSearchHeader';
import { LaunchDarklyFlags } from '../../launch-darkly';
import RiskIndicatorProfileHeader from '../RiskIndicators/RiskIndicatorProfileHeader';
import { useNavigation } from './NavigationProvider';
import { RequestAnalysisHeader } from '../Applications/RequestAnalysis/RequestAnalysisHeader';
import HelpDropDownMenu from '../Settings/HelpDropDownMenu';

interface NavigationProps {
  title: string;
  children: JSX.Element;
}

export function Navigation({ title, children }: NavigationProps) {
  const [open, setOpen] = useState(true);
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState(null);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = useState(null);
  const {
    appName,
    deviceDetails: { deviceId, deviceName },
  } = useNavigation();

  const toggleDrawer = () => {
    setOpen(!open);
  };
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  const navItems = [
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_DASHBOARD),
      path: '/dashboard',
      label: 'Dashboards',
      icon: <DashboardIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_DEVICES),
      path: '/devices',
      label: 'Devices',
      icon: <DevicesIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_DEVICE_OWNERS),
      path: '/device-owners',
      label: 'Device Owners',
      icon: <DeviceOwnersIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_APPLICATIONS),
      path: '/applications',
      label: 'Applications',
      icon: <AppsIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_REQUEST_ANALYSIS),
      path: '/request-analysis',
      label: 'Request Analysis',
      icon: <RequestAnalysisIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_POLICIES),
      path: '/policies',
      label: 'Policies',
      icon: <PoliciesIcon />,
    },
    {
      hasPermissions: flags.managedApps && verifyAuthPermission(AuthPermissions.MENU_MANAGED_APPS),
      path: '/app-management',
      label: 'App Management',
      icon: <AppManagementIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_RISK_INDICATORS),
      path: '/risk-indicators',
      label: 'Risk Indicators',
      icon: <RiskIndicatorsIcon />,
    },
    {
      hasPermissions: verifyAuthPermission(AuthPermissions.MENU_CUSTOMER_SUPPORT),
      path: '/customer-support',
      label: 'Customer Support',
      icon: <CustomerSupportIcon />,
    },
  ];

  const location = useLocation();

  const CustomHeader = () => {
    if (location.pathname === '/dashboard') {
      return <StyledHeaderText>Dashboards</StyledHeaderText>;
    }

    if (location.pathname.match(/\/policies\/create/g)) {
      return <StyledHeaderText>Create Policy</StyledHeaderText>;
    }

    if (location.pathname.match(/\/policies\/edit/g)) {
      return <StyledHeaderText>Edit Policy</StyledHeaderText>;
    }

    if (location.pathname.match(/\/policies\/view/g)) {
      return <PolicyProfileHeader />;
    }

    if (location.pathname.includes('/device-details')) {
      return <DeviceProfileHeader deviceName={deviceName} deviceId={deviceId} />;
    }

    if (location.pathname.includes('/application-details')) {
      return <StyledHeaderText>{appName}</StyledHeaderText>;
    }

    if (location.pathname.includes('/app-detail-report')) {
      return <StyledHeaderText>{appName}</StyledHeaderText>;
    }

    if (location.pathname.includes('/new-report-pdf-view')) {
      return <StyledHeaderText>{appName}</StyledHeaderText>;
    }

    if (location.pathname.match(/\/app-management\/(add|search|edit)/g)) {
      return <AppManagementSearchHeader />;
    }

    if (location.pathname.match(/\/risk-indicators\/edit/g)) {
      return <RiskIndicatorProfileHeader />;
    }

    if (location.pathname.includes('/organization-mismatch-error')) {
      return <StyledHeaderText />;
    }

    if (location.pathname.startsWith('/request-analysis')) {
      return <RequestAnalysisHeader />;
    }

    return <StyledHeaderText>{title}</StyledHeaderText>;
  };

  const { isAuthenticated, isLoading, user } = useAuth0();

  const handleSettingsDropDownMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setSettingsMenuAnchorEl(e.currentTarget);
  };

  const handleSettingsDropDownMenuClose = () => {
    setSettingsMenuAnchorEl(null);
  };

  const handleHelpDropDownMenuClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setHelpMenuAnchorEl(e.currentTarget);
  }
  const handleHelpDropDownMenuClose = () => {
    setHelpMenuAnchorEl(null);
  }

  return (
    <StyledBox>
      <StyledAppBar position="fixed" open={open}>
        <StyledToolbar>
          <ToggleIconButton color="inherit" aria-label="open drawer" onClick={toggleDrawer} open={open}>
            <StyledMenuIcon />
          </ToggleIconButton>
          <CustomHeader />
          <QHeaderIconButton onClick={(e) => handleHelpDropDownMenuClick(e)} aria-label="help" sx={{ marginRight: '16px' }}>
            <HelpOutlineIcon />
          </QHeaderIconButton>
          <StyledAvatar
            alt={isAuthenticated && !isLoading && user ? user.name : ''}
            src={isAuthenticated && !isLoading && user ? user.picture : ''}
            onClick={e => handleSettingsDropDownMenuClick(e)}
            data-testid="user-avatar"
          />
          <HelpDropDownMenu anchorEl={helpMenuAnchorEl} handleClose={handleHelpDropDownMenuClose} />
          <SettingsDropDownMenu anchorEl={settingsMenuAnchorEl} handleClose={handleSettingsDropDownMenuClose} />
        </StyledToolbar>
      </StyledAppBar>


      <StyledDrawer open={open}>
        <StyledToolbar component={Link} href="/">
          <img src="/logo/Quokka_Logo_RGB_wht.svg" alt="quokka-logo" width={128} height={56} />
        </StyledToolbar>
        <StyledDivider />
        <nav>
          <StyledMenuList>
            {navItems.map(
              ({ path, label, icon, hasPermissions }) =>
                hasPermissions && (
                  <StyledMenuItem current={location.pathname.startsWith(path)} key={path}>
                    <StyledLink to={path} href={path}>
                      <StyledListItemIcon>{icon}</StyledListItemIcon>
                      <NavListItemText primary={label} />
                    </StyledLink>
                  </StyledMenuItem>
                ),
            )}
          </StyledMenuList>
        </nav>
      </StyledDrawer>

      <StyledMain>
        {/** placeholder to offset the top of the container */}
        <StyledToolbar />
        {children}
      </StyledMain>
    </StyledBox>
  );
}

/** styles */
const StyledAvatar = styled(Avatar)`
  height: 35px;
  width: 35px;
  background-color: rgb(183, 194, 248);
  & img {
    margin-left: 0;
  }
  &:hover {
    cursor: pointer;
  }
`;

const StyledToolbar = styled(QToolbar)(() => ({
  display: 'flex',
  color: 'black',
  paddingLeft: '0 !important',
  paddingRight: 0,
}));

const StyledMenuList = styled(MenuList)`
  padding-top: 16px;
`;

const StyledMenuIcon = styled(MenuIcon)`
  color: black;
`;

const StyledListItemIcon = styled(ListItemIcon)`
  color: white;
  min-width: 0;
  padding-right: 11px;
`;

const StyledLink = styled(Link)`
  display: flex;
  padding: 20px 18px 17px 18px;
  width: 100%;
  text-decoration: none;
`;

const selectedNavStyles = props => {
  return css`
    .MuiSvgIcon-root {
      fill: ${props.theme.palette.accent.redOrange};
    }
    .MuiListItemText-root {
      color: ${props.theme.palette.accent.redOrange};
    }
    &::before {
      content: '';
      width: 6px;
      height: 61px;
      background-color: ${props.theme.palette.accent.redOrange};
      position: absolute;
      left: 0;
      top: 0;
    }
  `;
};

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'current',
})<{ current: boolean }>`
  padding: 0;
  &:hover {
    ${selectedNavStyles}
  }
  ${props => props.current && selectedNavStyles(props)}
`;

const StyledHeaderText = styled(QTypography)`
  flex-grow: 1;
  line-height: 45px;
  color: ${props => props.theme.palette.text.secondary};
  // shorten leading trim on leftside to offset default whitespace
  margin-left: -4px;
` as typeof QTypography;

// @ts-ignore
StyledHeaderText.defaultProps = {
  component: 'h1',
  variant: 'h1Regular',
};

const StyledBox = styled(Box)`
  display: grid;

  grid-template:
    'header            header' fit-content(100%)
    'drawer             main' auto
    / fit-content(100%) auto;

  background-color: white;
  height: 100vh;
`;

const StyledDivider = styled(Divider)`
  border-color: white;
`;

const ToggleIconButton = styled(IconButton)<{ open: boolean }>`
  ${props => (props.open ? 'display: none;' : '')};
  margin-right: 31px;
  width: 55px;
`;

const StyledAppBar = styled(KwAppBar)`
  && {
    grid-area: header;
    padding: ${props => props.open && '0px 30px'};
  }
`;

const StyledDrawer = styled(DashboardDrawer)`
  grid-area: drawer;
  width: ${drawerWidth}px;
  box-shadow: 4px 0px 4px rgb(0, 0, 0, 0.25);
  z-index: 1;
`;

StyledDrawer.defaultProps = {
  variant: 'permanent',
};

const StyledMain = styled((props: any) => <Box {...props} component="main" />)`
  grid-area: main;
`;
